import { useState } from 'react';
import {
	BusinessTabIconSelected,
	BusinessTabIcon,
	CalendarGreenCheck,
	CalendarRedX,
	LeadGreen,
	LeadRed,
	MessageBubbles,
	MessageBubblesGreenCheck,
	MessageBubblesRedX,
	RadioButtonEmptyWhite,
	RadioButtonSelectedGold
} from '@/resources/assets/images';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';
import {
	BusinessQuery,
	Filter,
	FilterQueryType,
	FilterType
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import omit from 'lodash/omit';
import { isQueryDifferent } from '../src/helpers/isQueryDifferent';
import SearchTabFiltersListItemHeader from '../SearchTabFiltersListItemHeader';
import { useSearchTabContext } from '../SearchTabContext';
import './SearchTabContactStatus.scss';

type SearchTabContactStatusProps = {
	onFilterChange: (query: BusinessQuery) => void;
	onGetBusiness: (query: BusinessQuery) => void;
};

const SearchTabContactStatus = ({
	onFilterChange,
	onGetBusiness
}: SearchTabContactStatusProps) => {
	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const [contactStatusQuery, setContactStatusQuery] = useState<
		BusinessQuery | {}
	>({});

	const { selectedListItem } = useSearchTabContext();

	const clearFilters = () => {
		const newQuery = { ...query };

		delete newQuery.contactedYes;
		delete newQuery.contactedNo;
		delete newQuery.scheduledYes;
		delete newQuery.scheduledNo;
		delete newQuery.isInListYes;
		delete newQuery.isInListNo;
		delete newQuery.emailsYes;
		delete newQuery.emailsNo;
		delete newQuery.messagesYes;
		delete newQuery.messagesNo;
		delete newQuery.lead;

		newQuery.skip = 0;

		sendAnalytics({
			type: 'track',
			userId: user.email,
			properties: {
				query: newQuery
			},
			eventName: 'User cleared contact status filters'
		});

		onFilterChange(newQuery);

		const queryChanged = isQueryDifferent(newQuery);

		if (queryChanged) {
			onGetBusiness(newQuery);
		}

		setContactStatusQuery({});
	};

	const queryChange = (filter: Filter) => {
		const queryNew = { ...query, user };

		if (filter.value === 'contacted') {
			if (filter.type === FilterType.INCLUDES) {
				setContactStatusQuery(
					omit(
						{ ...contactStatusQuery, contactedYes: true },
						'contactedNo'
					)
				);
				queryNew.contactedYes = true;
				delete queryNew.contactedNo;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Contacted',
							value: 'Included',
							query: queryNew
						}
					});
				}
			}

			if (filter.type === FilterType.EXCLUDES) {
				setContactStatusQuery(
					omit(
						{ ...contactStatusQuery, contactedNo: true },
						'contactedYes'
					)
				);
				queryNew.contactedNo = true;
				delete queryNew.contactedYes;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Contacted',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'scheduled') {
			if (filter.type === FilterType.INCLUDES) {
				setContactStatusQuery(
					omit(
						{ ...contactStatusQuery, scheduledYes: true },
						'scheduledNo'
					)
				);
				queryNew.scheduledYes = true;
				delete queryNew.scheduledNo;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Scheduled',
							value: 'Included',
							query: queryNew
						}
					});
				}
			}

			if (filter.type === FilterType.EXCLUDES) {
				setContactStatusQuery(
					omit(
						{ ...contactStatusQuery, scheduledNo: true },
						'scheduledYes'
					)
				);
				queryNew.scheduledNo = true;
				delete queryNew.scheduledYes;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Scheduled',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'isInList') {
			if (filter.type === FilterType.INCLUDES) {
				setContactStatusQuery({
					...contactStatusQuery,
					isInListYes: true
				});
				queryNew.isInListYes = true;
				delete queryNew.isInListNo;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Is in a List',
							value: 'Included',
							query: queryNew
						}
					});
				}
			}

			if (filter.type === FilterType.EXCLUDES) {
				setContactStatusQuery({
					...contactStatusQuery,
					isInListNo: true
				});
				queryNew.isInListNo = true;
				delete queryNew.isInListYes;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Is in a List',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'lead') {
			if (filter.type === FilterType.INCLUDES) {
				setContactStatusQuery({ ...contactStatusQuery, lead: true });
				queryNew.lead = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Lead',
							value: 'Included',
							query: queryNew
						}
					});
				}
			}

			if (filter.type === FilterType.EXCLUDES) {
				setContactStatusQuery({ ...contactStatusQuery, lead: false });
				queryNew.lead = false;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Lead',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			}

			if (filter.type === FilterType.NEUTRAL) {
				setContactStatusQuery({
					...contactStatusQuery,
					lead: undefined
				});
				queryNew.lead = undefined;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Lead',
							value: 'Excluded',
							query: queryNew
						}
					});
				}
			}
		}

		queryNew.limit = 50;

		queryNew.skip = 0;

		queryNew.queryType = FilterQueryType.FILTER;

		onFilterChange(queryNew);
		onGetBusiness(queryNew);
	};

	const filtersCount = () => {
		let count = 0;

		if (query?.contactedYes || query?.contactedNo) {
			count++;
		}

		if (query?.scheduledYes || query?.scheduledNo) {
			count++;
		}

		if (query?.isInListYes || query?.isInListNo) {
			count++;
		}

		if (query?.lead !== undefined) {
			count++;
		}

		return count;
	};

	return (
		<div
			className={`search-tab-filters-list-item ${selectedListItem === 'Contact Status' ? 'opened' : ''} ${filtersCount() > 0 ? 'filter-selected' : ''} `}
		>
			<SearchTabFiltersListItemHeader
				filterName="Contact Status"
				filterCount={filtersCount()}
				onClick={clearFilters}
				displayClearFiltersButton={filtersCount() > 0}
				icon={
					<MessageBubbles className="search-tab-filter-list-item-icon message-bubbles" />
				}
			/>
			<div className="search-tab-filters-list-item-dropdown">
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group first">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header">
						Contact Status
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.contactedYes || query.contactedNo ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.INCLUDES,
										value: 'contacted'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<MessageBubblesGreenCheck />
							Contacted
						</span>
					</div>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.contactedNo || query.contactedYes ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.EXCLUDES,
										value: 'contacted'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<MessageBubblesRedX />
							Not Contacted
						</span>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header">
						Scheduled Status
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.scheduledYes || query.scheduledNo ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.INCLUDES,
										value: 'scheduled'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<CalendarGreenCheck />
							Scheduled
						</span>
					</div>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.scheduledNo || query.scheduledYes ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.EXCLUDES,
										value: 'scheduled'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<CalendarRedX />
							Not Scheduled
						</span>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header">
						In a List
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-is-in-a-list">
						{!query.isInListYes || query.isInListNo ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.INCLUDES,
										value: 'isInList'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-is-in-a-list-inner">
							<BusinessTabIconSelected /> In a List
						</span>
					</div>
					<div className="search-tab-filters-list-item-dropdown-filter-group-is-in-a-list">
						{!query.isInListNo || query.isInListYes ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.EXCLUDES,
										value: 'isInList'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-is-in-a-list-inner">
							<BusinessTabIcon /> Not In a List
						</span>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header">
						Lead Status
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.lead ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.INCLUDES,
										value: 'lead'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<LeadGreen />
							Lead
						</span>
					</div>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{query.lead || query.lead === undefined ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.EXCLUDES,
										value: 'lead'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<LeadRed />
							No Lead
						</span>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
			</div>
		</div>
	);
};

export default SearchTabContactStatus;
